import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { inputCls, ImageManager, fullUrl, canDo } from "./shared";

const GENDER_LABELS = { men: "Men", women: "Women", unisex: "Unisex" };

export function CategoriesTab({ user, reloadCats }) {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", image: "", order: 0, visible: true, subs: "", gender: "unisex" });

  const load = () => api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const canEdit = canDo(user, "categories", "edit");
  const canCreate = canDo(user, "categories", "create");
  const canDelete = canDo(user, "categories", "delete");

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, order: parseInt(form.order) || 0, subs: String(form.subs).split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing === "new") await api.post("/admin/categories", payload);
      else await api.put(`/admin/categories/${editing.id}`, payload);
      toast.success("Collection saved — live on the store");
      setEditing(null);
      load();
      reloadCats();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove collection "${c.name}"? Products stay but lose this grouping.`)) return;
    await api.delete(`/admin/categories/${c.id}`).catch((e) => toast.error(formatApiError(e)));
    load();
    reloadCats();
  };

  const toggleVisible = async (c) => {
    await api.put(`/admin/categories/${c.id}`, { name: c.name, image: c.image || "", order: c.order, visible: !c.visible, subs: c.subs || [], gender: c.gender || "unisex" }).catch((e) => toast.error(formatApiError(e)));
    load();
    reloadCats();
  };

  const groups = [["men", "Men"], ["women", "Women"], ["unisex", "Unisex / Both"]];

  return (
    <div className="max-w-3xl">
      {canCreate && (
        <button data-testid="admin-add-category-button" onClick={() => { setForm({ name: "", image: "", order: cats.length, visible: true, subs: "", gender: "men" }); setEditing("new"); }} className="vv-btn-dark mb-8">
          <Plus size={12} /> Add Collection
        </button>
      )}
      {groups.map(([g, label]) => {
        const list = cats.filter((c) => (c.gender || "unisex") === g);
        if (!list.length) return null;
        return (
          <div key={g} className="mb-8">
            <p className="vv-overline text-vv-muted mb-3" data-testid={`category-group-${g}`}>{label}</p>
            <div className="space-y-3">
              {list.map((c) => (
                <div key={c.id} data-testid={`admin-category-${c.slug}`} className="border border-vv-black/10 p-4 flex items-center gap-4">
                  <div className="w-12 h-14 bg-vv-elevated overflow-hidden shrink-0">
                    {c.image && <img src={fullUrl(c.image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{c.name} <span className="text-vv-stone font-mono text-xs">/{c.slug}</span></p>
                    <p className="text-[11px] text-vv-muted">Order {c.order} · {(c.subs || []).join(", ") || "no subcollections"}</p>
                  </div>
                  {canEdit && (
                    <button data-testid={`category-visible-${c.slug}`} onClick={() => toggleVisible(c)}
                      className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${c.visible ? "border-vv-forest text-vv-forest" : "border-vv-stone text-vv-stone"}`}>
                      {c.visible ? <Eye size={12} /> : <EyeOff size={12} />} {c.visible ? "Live" : "Hidden"}
                    </button>
                  )}
                  {canEdit && (
                    <button data-testid={`category-edit-${c.slug}`} onClick={() => { setForm({ name: c.name, image: c.image || "", order: c.order, visible: c.visible, subs: (c.subs || []).join(", "), gender: c.gender || "unisex" }); setEditing(c); }} className="p-2" aria-label="Edit"><Pencil size={14} strokeWidth={1.5} /></button>
                  )}
                  {canDelete && (
                    <button data-testid={`category-delete-${c.slug}`} onClick={() => remove(c)} className="p-2 text-vv-burgundy" aria-label="Delete"><Trash2 size={14} strokeWidth={1.5} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-vv-black/40" onClick={() => setEditing(null)} />
          <form data-testid="category-form" onSubmit={save} className="relative bg-white w-full max-w-md p-8 space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-display text-2xl">{editing === "new" ? "New Collection" : "Edit Collection"}</p>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close"><X size={20} strokeWidth={1.25} /></button>
            </div>
            <input data-testid="category-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Collection name" className={inputCls} />
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">Collection for</label>
              <select data-testid="category-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className={`${inputCls} mt-2`}>
                <option value="men">Men only</option>
                <option value="women">Women only</option>
                <option value="unisex">Unisex — both</option>
              </select>
            </div>
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} placeholder="Display order (0 = first)" className={inputCls} />
            <input data-testid="category-subs" value={form.subs} onChange={(e) => setForm({ ...form, subs: e.target.value })} placeholder="Subcollections, comma separated" className={inputCls} />
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">Tile image</label>
              <div className="mt-2">
                <ImageManager images={form.image ? [form.image] : []} onChange={(imgs) => setForm({ ...form, image: imgs[0] || "" })} />
              </div>
            </div>
            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} className="accent-[#111111]" />
              Visible on storefront
            </label>
            <button data-testid="category-save-button" type="submit" className="vv-btn-dark w-full">Save Collection</button>
          </form>
        </div>
      )}
    </div>
  );
}
